/**
 * Provider Store API Data Source
 * Data source for provider-specific store endpoints
 * These endpoints automatically filter stores based on the provider's assigned brands
 */

import {Http} from '../../../Data/config/Http';
import {StoreDTO} from '../models/store.dto';
import {Bounds} from '../../../Presentation/components/common/types';
import {API_ROUTES} from '../../../constants/apiRoutes';
import type {BaseResponse} from '../../../Data/types/BaseResponse';
import {unwrapResponse, handleDataSourceError} from '../../../utils/errorHelpers';

export const ProviderStoreAPIDataSource = {
  /**
   * Get nearby stores for the current provider
   * This endpoint automatically filters stores based on provider's assigned brands
   *
   * @param bounds - Bounding box coordinates
   * @param options - Additional options (city, region, limit, includeDetails)
   * @returns Array of stores that have inventory from provider's assigned brands
   */
  getMyNearbyStores: async (
    bounds: Bounds,
    options?: {
      city?: string;
      region?: string;
      limit?: number;
      includeDetails?: boolean;
    }
  ): Promise<StoreDTO[]> => {
    try {
      const params: Record<string, any> = {
        sw_lat: bounds.sw.lat,
        sw_lng: bounds.sw.lng,
        ne_lat: bounds.ne.lat,
        ne_lng: bounds.ne.lng,
        limit: options?.limit || 1000,
        includeDetails: options?.includeDetails ?? true
      };

      if (options?.city) {
        params.city = options.city;
      }

      if (options?.region) {
        params.region = options.region;
      }

      const response = await Http.get<BaseResponse<StoreDTO[]>>(
        API_ROUTES.SERVICE_PROVIDERS.MY_NEARBY_STORES,
        {params}
      );

      const items = unwrapResponse(response.data, 'حدث خطأ في جلب متاجر المزود');
      return Array.isArray(items) ? items : [];
    } catch (error) {
      handleDataSourceError(error);
    }
  },

  /**
   * Get store brand details for a specific store and brand
   *
   * @param storeId - Store ID
   * @param brandId - Brand ID
   * @returns Store brand details including products and analytics
   */
  getStoreBrandDetails: async (
    storeId: string,
    brandId: string
  ): Promise<any> => {
    try {
      const response = await Http.get<BaseResponse<any>>(
        API_ROUTES.SERVICE_PROVIDERS.STORE_BRAND_DETAILS(storeId, brandId)
      );
      return unwrapResponse(response.data, 'حدث خطأ في جلب تفاصيل متجر البراند');
    } catch (error) {
      handleDataSourceError(error);
    }
  }
};

